import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, Users, DollarSign, Video } from "lucide-react";

interface Session {
  id: string;
  title: string;
  description: string;
  session_type: string;
  duration_minutes: number;
  price: number;
  max_attendees: number;
  start_time: string;
  end_time: string;
  meeting_link: string | null;
  status: string;
  host_id: string;
}

const DashboardSessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    loadSession();
    checkBooking();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const { data, error } = await supabase
        .from("live_sessions")
        .select("*")
        .eq("id", sessionId)
        .single();

      if (error) throw error;
      setSession(data);
    } catch (error: any) {
      toast.error("Failed to load session");
      console.error(error);
      navigate("/dashboard/sessions");
    } finally {
      setLoading(false);
    }
  };

  const checkBooking = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("session_bookings")
        .select("*")
        .eq("session_id", sessionId)
        .eq("user_id", user.id)
        .single();

      setIsBooked(!!data);
    } catch (error) {
      // No booking found
    }
  };

  const bookSession = async () => {
    setBooking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { error } = await supabase.from("session_bookings").insert({
        session_id: sessionId,
        user_id: user.id,
        payment_status: session?.price === 0 ? "paid" : "pending",
        payment_amount: session?.price || 0,
      });

      if (error) throw error;

      toast.success("Session booked successfully!");
      setIsBooked(true);
    } catch (error: any) {
      toast.error(error.message || "Failed to book session");
      console.error(error);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate("/dashboard/sessions")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sessions
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{session.title}</CardTitle>
                <CardDescription>{session.description}</CardDescription>
              </div>
              <Badge variant={session.status === "scheduled" ? "default" : "secondary"}>
                {session.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {new Date(session.start_time).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">
                    {new Date(session.start_time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">{session.duration_minutes} minutes</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Max Attendees</p>
                  <p className="font-medium">{session.max_attendees}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Price</p>
                  <p className="font-medium">
                    {session.price === 0 ? "Free" : `$${(session.price / 100).toFixed(2)}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{session.session_type}</p>
                </div>
              </div>
            </div>

            {isBooked && session.meeting_link && (
              <div className="p-4 bg-primary/10 rounded-lg">
                <p className="text-sm font-medium mb-2">Meeting Link:</p>
                <a
                  href={session.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {session.meeting_link}
                </a>
              </div>
            )}

            <div className="flex gap-2">
              {!isBooked ? (
                <Button onClick={bookSession} disabled={booking} className="w-full">
                  {booking ? "Booking..." : session.price === 0 ? "Book Free Session" : "Book & Pay"}
                </Button>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Already Booked
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardSessionDetail;
